import { useEffect, useState } from 'react';
import { StringUtils } from '@manifoldxyz/js-ts-utils';
import { ClaimStudioInstance } from '@/api/models/studioInstance';
import useMainStore from '@/store/mainStore';
import Modal from './Modal';

export default function YoutubeEmbed() {
  const studioInstance = useMainStore((state) => state.studioInstance);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [videoTitle, setVideoTitle] = useState<string>('YouTube Video');

  // Extract YouTube video ID from URL
  const getYoutubeVideoId = (url: string): string | null => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const videoSrc =
    (studioInstance as ClaimStudioInstance)?.publicData.clientTheme?.videoEmbed?.src || '';
  const videoId = getYoutubeVideoId(videoSrc);

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  // Fetch video title from YouTube
  useEffect(() => {
    const fetchVideoTitle = async () => {
      try {
        // Try to get video details from the oEmbed endpoint (doesn't require API key)
        const response = await fetch(
          `https://noembed.com/embed?url=https://www.youtube.com/watch?v=${videoId}`,
        );

        if (response.ok) {
          const data = await response.json();
          if (data.title) {
            setVideoTitle(data.title);
          }
        }
      } catch (error) {
        console.error('Error fetching video title:', error);
        // Fallback to a generic title
        setVideoTitle('YouTube Video');
      }
    };

    fetchVideoTitle();
  }, [videoId]);

  const handleOpenLightbox = () => {
    setIsLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setIsLightboxOpen(false);
  };

  // If there's no video source, don't render anything
  if (!(studioInstance as ClaimStudioInstance)?.publicData.clientTheme?.videoEmbed?.src) {
    return null;
  }

  // If we couldn't extract a valid video ID, don't render anything
  if (!videoId) {
    return null;
  }

  return (
    <div className="m-claim--w-full">
      {/* Video Card */}
      <button
        onClick={handleOpenLightbox}
        className={StringUtils.classnames(
          'm-claim--w-full',
          'm-claim--bg-[#101010]',
          'm-claim--rounded-lg',
          'm-claim--overflow-hidden',
          'm-claim--transition-all',
          'm-claim--duration-200',
          'm-claim--shadow-md',
          'm-claim--border',
          'm-claim--border-gray-800',
          'hover:m-claim--shadow-lg',
          'hover:m-claim--border-gray-700',
          'm-claim--relative',
          'm-claim--text-left',
          'm-claim--flex',
          'm-claim--flex-col',
          'm-claim--outline-none',
          'm-claim--focus:outline-none',
          'm-claim--focus:ring-2',
          'm-claim--focus:ring-primary/50',
        )}
        aria-label={`Play ${videoTitle}`}
        tabIndex={0}
      >
        {/* Thumbnail Container */}
        <div className="m-claim--relative m-claim--aspect-video m-claim--w-full m-claim--overflow-hidden">
          <img
            src={thumbnailUrl}
            alt={videoTitle}
            className="m-claim--size-full m-claim--object-cover"
          />

          {/* Play Button Overlay */}
          <div
            className={StringUtils.classnames(
              'm-claim--absolute',
              'm-claim--inset-0',
              'm-claim--flex',
              'm-claim--items-center',
              'm-claim--justify-center',
              'm-claim--transition-all',
              'm-claim--duration-200',
              'm-claim--bg-[rgba(0,0,0,0.3)]',
              'hover:m-claim--bg-[rgba(0,0,0,0.4)]',
            )}
          >
            <div
              className={StringUtils.classnames(
                'm-claim--flex',
                'm-claim--size-16',
                'm-claim--items-center',
                'm-claim--justify-center',
                'm-claim--rounded-full',
                'm-claim--border-2',
                'm-claim--bg-[rgba(0,0,0,0.6)]',
                'm-claim--border-[#ffffff]',
              )}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="m-claim--size-8 m-claim--text-[#ffffff]"
              >
                <path
                  d="M8 5.14v14l11-7-11-7z"
                  fill="currentColor"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Video Title */}
        <div className="m-claim--p-4">
          <h3 className="m-claim--font-medium m-claim--text-[#ffffff]">{videoTitle}</h3>
          <p className="m-claim--mt-1 m-claim--text-sm m-claim--text-[#9ca3af]">
            Click to watch video
          </p>
        </div>
      </button>

      {/* YouTube Video Lightbox Modal */}
      <Modal
        isOpen={isLightboxOpen}
        onClose={handleCloseLightbox}
        size="lg"
      >
        <div className="m-claim--flex m-claim--size-full m-claim--items-center m-claim--p-0">
          <div className="m-claim--aspect-video m-claim--w-full m-claim--overflow-hidden m-claim--rounded-lg">
            <iframe
              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
              title={videoTitle}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="m-claim--size-full m-claim--border-0"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
